import React, { useState } from 'react';
import { CheckCircle, XCircle, Building2 } from 'lucide-react';
import SearchBar from '../../../components/SearchBar';
import { text } from '../constants/configurationText';

const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  return words.length === 1
    ? words[0].slice(0, 2).toUpperCase()
    : (words[0][0] + words[1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  '#2e7cba', '#10b981', '#f59e0b', '#8b5cf6',
  '#ef4444', '#0ea5e9', '#14b8a6', '#f97316',
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const CompanyList = ({ companies, selectedCompany, theme, styles, t = text.en, language = 'en', onSelect }) => {
  const [search, setSearch] = useState('');

  const filtered = companies.filter(c =>
    c.clientName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ ...styles.card, marginBottom: 0, padding: '20px' }}>
      {/* Header */}
      <div style={{ ...styles.sectionHeader, marginBottom: '16px', paddingBottom: '14px' }}>
        <h3 style={{ ...styles.sectionHeading, fontSize: '16px' }}>
          <Building2 size={18} />
          {t.companies}
        </h3>
        <span style={styles.countPill}>{companies.length}</span>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '14px' }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder={t.searchCompanies}
          theme={theme === 'light' ? 'light' : 'dark'}
          language={language}
          icon
        />
      </div>

      {/* List */}
      <div className="cfg-scroll" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: styles.colors.faint, fontSize: '13.5px' }}>
            {t.noCompanies}
          </div>
        )}

        {filtered.map(company => {
          const isSelected = selectedCompany === company.clientName;
          const avatarColor = getAvatarColor(company.clientName);
          return (
            <div
              key={company.clientName}
              className="cfg-company"
              style={styles.companyCard(isSelected)}
              onClick={() => onSelect(company.clientName)}
            >
              {/* Avatar */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: isSelected ? avatarColor : `${avatarColor}22`,
                color: isSelected ? '#ffffff' : avatarColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: '800',
                flexShrink: 0,
                transition: 'all 0.2s',
              }}>
                {getInitials(company.clientName)}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  ...styles.companyName,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {company.clientName}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                  <div style={styles.companyStatus(company.isActive)}>
                    {company.isActive
                      ? <CheckCircle size={12} />
                      : <XCircle size={12} />}
                    {company.isActive ? t.active : t.inactive}
                  </div>
                  {company.subscriptionEndDate && (
                    <span style={{ fontSize: '11.5px', color: styles.colors.faint }}>
                      · {new Date(company.subscriptionEndDate).toLocaleDateString(language === 'ar' ? 'ar-EG-u-nu-latn' : undefined)}
                    </span>
                  )}
                </div>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: styles.brand,
                  flexShrink: 0,
                }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompanyList;